import { BrowserModule } from '@angular/platform-browser';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SharedModule } from '@shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppIconsModule } from '@app/app-icons.module';
import { HTTP_INTERCEPTORS, HttpBackend } from '@angular/common/http';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { GuardService } from '@shared/services/guard.service';
import { provideTheme } from '@siemens/component-lib';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { catchError, map, of, take } from 'rxjs';
import { UnauthorizedInterceptor } from '@app/auth/services/unauthorized-interceptor.service';
import { environment } from '../environments/default/environment';
import { MainModule } from './main/main.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
    HttpLoaderFactory,
    ReportingToolMissingTranslationHandler
} from './core/translation/missing-translation.handler';

// Factory to create the MSAL instance with configuration from environment
const msalInstanceFactory = () => new PublicClientApplication(environment.msalConfig);

// Configure which API requests should have tokens attached, no need of token-injector-interceptor, so it is extremely important that you add your apiUrl to environment.apiConfig.uri
const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        protectedResourceMap.set(item.uri, item.scopes);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
};

// Configure the MSAL guard for route protection
const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig[1].scopes] },
    loginFailedRoute: '/unauthorized'
});

function initializeMsal(msalService: MsalService) {
    return () => {
        return msalService.handleRedirectObservable().pipe(
            map(() => {
                // Set the active account after login
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                return of(undefined);
            }),
            take(1)
        );
    };
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        DragDropModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpBackend]
            },
            isolate: true,
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: ReportingToolMissingTranslationHandler
            }
        }),
        AppIconsModule,
        AppRoutingModule,
        SharedModule.forRoot(),
        MainModule
    ],
    bootstrap: [AppComponent],
    providers: [
        provideTheme('sag'),
        // MSAL configuration providers
        {
            provide: MSAL_INSTANCE,
            useFactory: msalInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: msalGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: msalInterceptorConfigFactory
        },

        // MSAL services
        MsalService,
        MsalGuard,
        MsalBroadcastService,

        // Configure interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor, // Automatically adds auth tokens to requests
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor, // Handles 401 errors
            multi: true
        },

        // Initialize MSAL during app startup
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService));
            return initializerFn();
        })
    ]
})
export class AppModule {
    constructor(translate: TranslateService, guardService: GuardService) {
        translate.addLangs(['en', 'de']);
        translate.setDefaultLang('en');
        translate.use('en');

        registerLocaleData(localeDe, 'de');
        registerLocaleData(localeEn, 'en');

        guardService.currentUserSubscribable.subscribe(e => {
            translate.use(e?.settings?.language || 'en');
        });
    }
}
